// Stone theme constants extracted from prototypes/app.html and prototypes/client-dashboard.html
// Dark mode = default (:root). Light mode = .light-mode class on <html>.
// Tailwind uses darkMode: 'class' strategy with the class name 'light-mode' inverted —
// we follow the prototype pattern: dark is the base, light-mode class adds light overrides.

export type AgentId = 'google' | 'phone' | 'social' | 'web' | 'ads' | 'video' | 'nurture' | 'story'

// ─── CSS variables: NOT HERE ────────────────────────────────────────────────

/**
 * 🔴 THIS FILE USED TO HOLD A `CSS_VARS_DARK` / `CSS_VARS_LIGHT` TABLE. It was
 * DEAD — zero importers, repo-wide — and it cost an hour on 2026-09-20.
 *
 * Re-theming the Control Center to the brand teal, I edited it, watched nothing
 * change, and wrote a comment ON IT asserting the opposite: that these values are
 * "applied to the element at runtime, so a change made only in the stylesheet
 * silently does nothing." Exactly backwards. Nothing applied them anywhere. The
 * sidebar went teal when `app/control-center/src/index.css` changed, and I had
 * edited that in the same pass, so the wrong explanation fit the evidence.
 *
 * A dead table that looks like the token SOT is worse than no table: the next
 * session edits it, sees no change, and reaches for a different wrong story.
 *
 * THE REAL HOMES, both live:
 *   · app/shared/src/styles/design-system.css — the shared tokens (--sidebar-active,
 *     --bg-card, --border, the button classes). Shared by CC and admin.
 *   · app/control-center/src/index.css — CC-only tokens, plus the "JustTextAva
 *     accent" block near the bottom, which REDEFINES several of them and wins by
 *     source order. `--phase-active-bg` is declared FOUR times in that one file;
 *     only the last pair is the colour you see.
 *
 * So the brand colour is not one edit. Verify a re-theme against the BUILT css in
 * backend/hosting-public/control-center/assets/, never against the file you just
 * changed. [[verify_the_system_not_the_change]]
 */

// ─── Agent colors ────────────────────────────────────────────────────────────

// Canonical palette — must stay aligned with .badge-brand-* in design-system.css
// AND backend/functions/src/components/aiWorkforceOrgChartHtml.ts AGENT_META bgColor/borderColor.
// Drift fix shipped 2026-05-28: phone purple→cyan, social pink→purple, web amber→teal,
// ads green→orange, nurture teal→green, story teal→rose. Lockstep contract: changing any
// of these three sources requires updating the other two.
export const AGENT_COLORS: Record<AgentId, { bg: string; text: string; border: string; accent: string }> = {
  google:  { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   accent: '#3b82f6' },
  phone:   { bg: 'bg-cyan-50',   text: 'text-cyan-700',   border: 'border-cyan-200',   accent: '#06b6d4' },
  social:  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', accent: '#a855f7' },
  web:     { bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200',   accent: '#14b8a6' },
  ads:     { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', accent: '#f97316' },
  video:   { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    accent: '#ef4444' },
  nurture: { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  accent: '#22c55e' },
  story:   { bg: 'bg-rose-50',   text: 'text-rose-700',   border: 'border-rose-200',   accent: '#f43f5e' },
}

// Dark mode overrides for agent colors (used in card backgrounds on dark theme)
export const AGENT_COLORS_DARK: Record<AgentId, { bg: string; text: string; border: string }> = {
  google:  { bg: 'bg-blue-900/40',   text: 'text-blue-300',   border: 'border-blue-700' },
  phone:   { bg: 'bg-cyan-900/40',   text: 'text-cyan-300',   border: 'border-cyan-700' },
  social:  { bg: 'bg-purple-900/40', text: 'text-purple-300', border: 'border-purple-700' },
  web:     { bg: 'bg-teal-900/40',   text: 'text-teal-300',   border: 'border-teal-700' },
  ads:     { bg: 'bg-orange-900/40', text: 'text-orange-300', border: 'border-orange-700' },
  video:   { bg: 'bg-red-900/40',    text: 'text-red-300',    border: 'border-red-700' },
  nurture: { bg: 'bg-green-900/40',  text: 'text-green-300',  border: 'border-green-700' },
  story:   { bg: 'bg-rose-900/40',   text: 'text-rose-300',   border: 'border-rose-700' },
}

// Combined light + dark agent SURFACE classes for brand-tinted surfaces (agent
// detail header, etc.) that must adapt to dark mode. These are LITERAL strings
// (not runtime-constructed) so Tailwind JIT generates BOTH variants — a
// `dark:${var}` built at runtime is never scanned and silently dropped. The
// dark: variants fire on `.dark-mode` (see tailwind.config). Light values match
// AGENT_COLORS; dark values match AGENT_COLORS_DARK (keep in sync).
export const AGENT_SURFACE: Record<AgentId, { bg: string; text: string; border: string }> = {
  google:  { bg: 'bg-blue-50 dark:bg-blue-900/30',     text: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-200 dark:border-blue-800' },
  phone:   { bg: 'bg-cyan-50 dark:bg-cyan-900/30',     text: 'text-cyan-700 dark:text-cyan-300',     border: 'border-cyan-200 dark:border-cyan-800' },
  social:  { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' },
  web:     { bg: 'bg-teal-50 dark:bg-teal-900/30',     text: 'text-teal-700 dark:text-teal-300',     border: 'border-teal-200 dark:border-teal-800' },
  ads:     { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' },
  video:   { bg: 'bg-red-50 dark:bg-red-900/30',       text: 'text-red-700 dark:text-red-300',       border: 'border-red-200 dark:border-red-800' },
  nurture: { bg: 'bg-green-50 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-300',   border: 'border-green-200 dark:border-green-800' },
  story:   { bg: 'bg-rose-50 dark:bg-rose-900/30',     text: 'text-rose-700 dark:text-rose-300',     border: 'border-rose-200 dark:border-rose-800' },
}

// ─── Agent display names ─────────────────────────────────────────────────────

export const AGENT_DISPLAY_NAMES: Record<AgentId, string> = {
  google:  'Google Skill',
  phone:   'Phone Skill',
  social:  'Social Skill',
  web:     'Web Skill',
  ads:     'Ads Skill',
  video:   'Video Skill',
  nurture: 'Nurture Skill',
  story:   'Story Skill',
}

export const AGENT_DESCRIPTIONS: Record<AgentId, string> = {
  google:  'Google Business Profile management, geo-grid heatmaps, review requests, and AI search tracking.',
  phone:   'AI voice receptionist, website chat widget, appointment reminders, and lead qualification.',
  social:  'Facebook and Instagram organic posting, comment responses, and DM management.',
  web:     'Professional website creation, domain management, business email, and blog content.',
  ads:     'Google LSA and Meta Ads management with autonomous budget optimization.',
  video:   'YouTube channel management, video content creation, and cross-platform distribution.',
  nurture: 'Reactivation campaigns and ongoing personalized outreach to your customer database.',
  story:   'Quarterly video interview that produces 20-40 content pieces across all platforms.',
}

// ─── Behind-the-scenes agent names ──────────────────────────────────────────

export const BEHIND_THE_SCENES_AGENTS = [
  { id: 'it',           name: 'IT Agent',           description: 'Infrastructure monitoring, uptime, and automatic AI provider failover.' },
  { id: 'security',     name: 'Security Agent',      description: 'Data encryption, access control, threat monitoring, and audit trail integrity.' },
  { id: 'intelligence', name: 'Intelligence Agent',  description: 'Market intelligence, platform change detection, and competitor tracking.' },
  { id: 'optimization', name: 'Optimization Agent',  description: 'Performance tuning, cost optimization, and A/B testing.' },
  { id: 'compliance',   name: 'Compliance Agent',    description: 'Review policy compliance, ad platform ToS, and consent management.' },
] as const

// ─── Pricing ─────────────────────────────────────────────────────────────────
//
// Keystone Phase 2.6: AGENT_PRICE and BASE_PRICE are derived from the
// generated pricing module — which is regenerated on every React build
// from backend/functions/src/config/pricing.json (the canonical SOT).
// Previously these were hardcoded with BASE_PRICE = 50 — stale since
// the 04/04 $50 → $75 change and under-reporting every client's bill.
//
// Do NOT hardcode pricing literals. Edit the canonical JSON instead.

import { resolveMonthlyBillCents } from './monthlyBill'
import {
  basePriceMonthly,
  agentPriceMonthly,
  pricing as pricingSot,
} from './config/pricing.generated'

export {
  totalMonthly,
  fullStackMonthly,
  formatMonthly,
  formatMonthlyLong,
  formatUSD,
  pricing,
  type AgentKey,
} from './config/pricing.generated'

export const AGENT_PRICE = agentPriceMonthly('google')  // uniform agent pricing today
export const BASE_PRICE = basePriceMonthly()

// Referrer's ongoing monthly credit per ACTIVE referral — from the pricing SOT
// ($37.50 as of 2026-08-09). Distinct from AGENT_PRICE: the credit is NOT the
// skill price, and conflating them (Refer & Earn used to multiply activeCount ×
// AGENT_PRICE) breaks the moment the two diverge, which they now do. Per-client
// "referrals to free" is NOT a constant — the Control Center reads it, the live
// credit, and the carried balance from /api/referral-progress. This constant is
// for aggregate/admin views that sum credit across many referrers.
export const REFERRAL_CREDIT = pricingSot.referral.referrerCreditPerActiveReferralMonthly

// ── Client monthly bill — ONE source of truth ───────────────────────────────
// Used by BOTH the Settings billing panel and the Agents hire modal so the two
// always agree (Lesson 4 — don't keep two copies of the billing math). The hire
// modal previously hardcoded "Current: $200/mo"; this computes the client's
// ACTUAL bill from their hired-agent count instead.

/**
 * Platform-default promo discount % off the base fee — FROM THE PRICING SOT.
 *
 * WHY (2026-07-13). This was hardcoded to 100 — i.e. the base fee fully WAIVED — with a
 * standing "TODO: source from the pricing SOT". It was not just drift; it was a MONEY BUG in
 * the worst direction:
 *
 *   Stripe has NINE active prices at $75/mo, and one of them IS the base. We charge it.
 *   But computeMonthlyBill() applied monthlyBaseFee(100) = $0, so a client mid-onboarding
 *   (no live subscription yet → the fallback path) was quoted $75/mo LESS than they would
 *   actually be billed. They get charged MORE than their own dashboard said.
 *
 * An UNDERquote is worse than an overquote: an overquote is a pleasant surprise, an underquote
 * is a broken promise on their first invoice. It also contradicted every marketing surface —
 * justtextava and the live site both quote the base ("billed at $75/month").
 *
 * A 100% base waiver IS real — but it is a REFERRAL benefit (first month only) and an
 * AFFILIATE tier, not the platform default. A per-client promo must come from that client's
 * promo code, never from a global constant that silently applies to everyone.
 */
export const PROMO_DISCOUNT_PERCENT = pricingSot.base.defaultPromoDiscountPercent

/** Base fee after the current promo discount (≥100% off ⇒ $0). */
export function monthlyBaseFee(promoDiscountPercent: number = PROMO_DISCOUNT_PERCENT): number {
  return promoDiscountPercent >= 100 ? 0 : BASE_PRICE * (1 - promoDiscountPercent / 100)
}

/** List-price estimate of a monthly bill = base (after promo) + hired agents × agent price. */
export function computeMonthlyBill(
  hiredAgentCount: number,
  promoDiscountPercent: number = PROMO_DISCOUNT_PERCENT,
): number {
  return monthlyBaseFee(promoDiscountPercent) + Math.max(0, hiredAgentCount) * AGENT_PRICE
}

/**
 * A client's ACTUAL current monthly bill, in dollars. Prefers the real
 * Stripe-charged MRR on the client doc (`client.mrr`, in CENTS — computed by
 * stripeWebhook from the live subscription, so it already reflects their real
 * promo / discount / agents). Falls back to the list-price estimate only when
 * there's no live subscription yet (new signups mid-onboarding, demo data).
 *
 * This is what "what they actually pay each month" should read from — sourcing
 * the real charged amount instead of re-deriving the promo on the frontend.
 */
export function clientCurrentMonthly(
  mrrCents: number | null | undefined,
  hiredAgentCount: number,
): number {
  // ⚠️ `> 0` WAS `mrrCents > 0` AND THAT WAS THE BUG (fixed 2026-09-13, parity
  // Phase 1). A COMPED client has an mrr of exactly 0 — a known bill of nothing —
  // and 0 failed this test, so they fell through to the catalogue price. Todd's
  // billing page read "Monthly Total $600/mo" while he pays nothing. This was the
  // THIRD derivation of the same number; the other two disagreed with it and with
  // each other (Dashboard said $600, the weekly report said $150).
  //
  // 🔑 THE DISTINCTION WORTH KEEPING, and why the catalogue fallback SURVIVES:
  //   mrr is a number  -> they have a subscription. This is a BILL. Quote it.
  //   mrr is absent    -> no subscription yet (signup, plan preview, a prospect
  //                       picking skills). There is nothing to bill, so the
  //                       catalogue price is the right answer — it is a QUOTE.
  // Conflating those is what let an invented figure be rendered as a real bill.
  const cents = resolveMonthlyBillCents({ mrr: mrrCents })
  if (cents !== null) return cents / 100
  return computeMonthlyBill(hiredAgentCount)
}

export const PROMO_CODES: Record<string, { discountPercent: number; label: string }> = {
  BOISECHAMBER: { discountPercent: 100, label: 'Boise Metro Chamber' },
  IDAHOBBB:     { discountPercent: 75,  label: 'Idaho BBB' },
  BNI50:        { discountPercent: 50,  label: 'BNI' },
  WELCOME25:    { discountPercent: 25,  label: 'Welcome' },
}

// ─── Navigation ──────────────────────────────────────────────────────────────

/**
 * Super Admin sidebar nav — 11-tab IA (restructured 2026-06-03 to match the
 * prototypes/app.html sidebar, the locked source of truth).
 *
 * Route slugs intentionally differ from some prototype `currentPage` ids:
 *   - Health    → route `health`  (prototype id `support`)
 *   - Library   → route `library` (prototype id `templates`)
 * Everything else uses its prototype id as the slug.
 *
 * The old pre-restructure pages roll up here:
 *   - Analytics + Billing → Financial
 *   - Outreach            → Marketing
 *   - InternalWorkforce + WorkforceActivity → Workforce
 */
// Color emoji icons — mirror the prototype's navLinks (app.html).
export const ADMIN_NAV_ITEMS = [
  { id: 'dashboard',    label: 'Dashboard',    icon: '📊',  href: '/admin/dashboard' },
  { id: 'health',       label: 'Health',       icon: '🛡️',  href: '/admin/health' },
  { id: 'marketing',    label: 'Marketing',    icon: '📢',  href: '/admin/marketing' },
  { id: 'clients',      label: 'Clients',      icon: '👥',  href: '/admin/clients' },
  { id: 'agents',       label: 'Agents',       icon: '🤖',  href: '/admin/agents' },
  { id: 'financial',    label: 'Financial',    icon: '💰',  href: '/admin/financial' },
  { id: 'partners',     label: 'Partners',     icon: '🤝',  href: '/admin/partners' },
  { id: 'workforce',    label: 'Workforce',    icon: '🧠',  href: '/admin/workforce' },
  { id: 'lessons',      label: 'Lessons',      icon: '🎓',  href: '/admin/lessons' },
  // Celebrations moved into Experience as a sub-tab 2026-08-02 (see pages/Onboarding.tsx);
  // the /admin/celebrations route now redirects to /admin/onboarding-preview?tab=celebrations.
  { id: 'onboarding-preview', label: 'Experience', icon: '📨', href: '/admin/onboarding-preview' },
  { id: 'library',      label: 'Library',      icon: '📚',  href: '/admin/library' },
  { id: 'intelligence', label: 'Intelligence', icon: '🔭',  href: '/admin/intelligence' },
  { id: 'industry-knowledge', label: 'Industry Knowledge', icon: '📚', href: '/admin/industry-knowledge' },
  { id: 'settings',     label: 'Settings',     icon: '⚙️',  href: '/admin/settings' },
] as const

/**
 * Client Control Center sidebar nav (post-2026-05-26 IA redesign).
 *
 * Two structural changes from the prior layout:
 *   - `setup` is no longer in the static list — App.tsx prepends it dynamically
 *     as the FIRST item when the client has pending setup steps, and hides it
 *     entirely once everything is complete.
 *   - `reports`, `support`, and `connected-accounts` were demoted off the
 *     primary nav. Their pages still exist as fallback routes; their function
 *     moves to: Reports → Activity (new), Support → Settings > My Tickets
 *     sub-tab, Connected Accounts → Settings > Integrations sub-tab.
 *
 * `{ divider: true }` renders a horizontal rule in the sidebar (visual group
 * separator between primary nav and account/utility items).
 */
export const CLIENT_NAV_ITEMS = [
  { id: 'dashboard',      label: 'Dashboard',      icon: '🏠',  href: '/control-center/dashboard' },
  // ⚠️ APPROVALS SITS SECOND, DIRECTLY UNDER DASHBOARD (John, 2026-09-22 CC review).
  // It is the only tab that is a TO-DO LIST for the client — the one place where
  // delay costs them something — so it outranks the places they merely look at.
  { id: 'approvals',      label: 'Approvals',      icon: '✅',  href: '/control-center/approvals' },
  { id: 'customers',      label: 'My Customers',   icon: '👥',  href: '/control-center/customers' },
  // ⚠️ ACTIVITY SITS DIRECTLY UNDER MY CUSTOMERS (John, 2026-09-22 CC review).
  { id: 'activity',       label: 'Activity',       icon: '📊',  href: '/control-center/activity' },
  // ⚠️ EMAIL AND DOCUMENTS LEFT THE NAV 2026-09-20 (John, Control Center review).
  // Their ROUTES stay live, deliberately — dropping a route is how a working page
  // becomes unreachable, which is what happened to Scheduling on 09-19.
  //
  // EMAIL was redundant: a nurture/triage reply sets `replyTo` to the owner's own
  // address (measured: a real one on all four clients), so customers reply into
  // their normal inbox and they answer from Gmail. Its only unique value was Ava's
  // drafted reply. The INGESTION stays — it is what turns an email enquiry into a
  // lead — only the second inbox goes.
  //
  // DOCUMENTS moved to the CUSTOMER. John: "clients will access it by going to
  // their customer first, then create the necessary document from there." An
  // invoice lives with the person it is for, not in a filing cabinet. Its settings
  // moved to Settings. Measured at the time: 0 documents, 0 templates and NO
  // invoicingConfig for any of the four clients, and both Austin and Susan had
  // chosen `skillPreferences.invoicing: 'own'`.
  //
  // TEXTS STAYS, and it is not an activity log: the business texting number is
  // OURS, so this is the ONLY place a client can answer a customer who texted them.
  //
  // ⚠️ 2026-09-22 — DO NOT RETIRE THIS ON THE OLD TRIGGER. The line above used to
  // read "it retires when reply-by-text ships". Reply-by-text DID ship on 09-21
  // (`2c1431a2`, `owner-reply-to-customer-text-by-text`) — and reading what actually
  // shipped, the trigger was written expecting coverage the change does not have.
  //
  // `pendingCustomerReply` is staged ONLY when Ava ESCALATES, and its window is
  // enforced on read, so the owner can answer an escalation inside that window and
  // nothing else. He still cannot START a text to a customer, cannot answer a thread
  // Ava never escalated, and cannot answer after the window expires. This page is the
  // only door for all three.
  //
  // John confirmed KEEP on 2026-09-22 ("we probably still want the text as a
  // standalone in case they need a reply") — now evidence-backed rather than a hunch.
  // The real retirement trigger is owner-initiated + out-of-window replies working
  // from his phone, not the commit that shipped the escalation case.
  { id: 'texts',          label: 'Texts',          icon: '💬',  href: '/control-center/texts' },
  // Calls moved under Activity (CC review #2, 2026-09-22) — /calls redirects there.
  // ⚠️ AVA'S SKILLS LEFT THE NAV 2026-09-20 (John, Control Center review).
  //
  // The premise: we used to sell AGENTS, so "which agent did this" WAS the
  // product. We now sell ONE assistant with skills, so the client asks "what is
  // Ava doing for me" — and a per-agent DESTINATION is legacy framing. The
  // per-skill dimension survives as a LENS, not a place: the Dashboard's Skill
  // Activity card and the Activity page's skill filter.
  //
  // It went only once everything on it had a real home, because the alternative
  // is what happened to Scheduling on 09-19 — demoted "into the settings area",
  // never arrived, unreachable except by typing the URL:
  //   · approvals / connected accounts / needs-input → already elsewhere
  //   · per-skill CONFIGURATION → Settings (Phone + Website sub-tabs; social,
  //     google, ads and nurture as rows in Preferences)
  //   · call transcripts → the Calls page
  //   · buying and cancelling a skill → Billing ("does it change the bill?")
  //   · per-skill activity → the Dashboard card, ungated
  //
  // ⚠️ THE ROUTE STAYS LIVE and redirects. Every bookmark, magic link and older
  // Ava text pointing at /agents still lands somewhere sensible.
  // ⚠️ "Ava's Knowledge", not "Business Profile" (John, 2026-09-20). THREE things
  // in this product were called profile, and a client fixing a phone number could
  // have opened any of them: the Settings tab (renamed "Account" on 09-19), THIS
  // page, and "Google Business Profile" — a connected provider one tab over, which
  // keeps its name because it is Google's. We fixed half the collision on 09-19
  // and left this half. The name now says what the page IS: everything Ava knows
  // about the business, which is also the thing we most want them to come back and
  // confirm. The ROUTE stays `business-profile` — renaming it would break every
  // existing bookmark and magic link for a cosmetic gain.
  { id: 'business-profile', label: "Ava's Knowledge", icon: '📋', href: '/control-center/business-profile' },
  // ⚠️ SCHEDULING AND INTEGRATIONS LEFT THE SIDEBAR 2026-09-19 (John: "business
  // profile, scheduling, and integrations really all should be in the settings
  // area"). Both are configuration you set once and rarely revisit, which is what
  // Settings is for; the top-level nav is for what you USE.
  //
  // ⚠️ THE ROUTES ARE STILL LIVE ON PURPOSE. Only the nav entries went, so every
  // existing link, bookmark and magic link still resolves — dropping a route is
  // how a working page becomes unreachable, which is the failure this repo already
  // has a name for.
  //
  // BUSINESS PROFILE DELIBERATELY STAYS. It is not settings: it is what Ava KNOWS
  // (services, prices, hours, FAQs — the confirmed facts Spec enforces before
  // anything is published), and it is the surface we most need a client to come
  // back and CONFIRM. Burying the one page we want them to visit would work
  // against the client sign-off design.
  { divider: true },
  { id: 'refer-and-earn', label: 'Refer & Earn',   icon: '🎁',  href: '/control-center/refer-and-earn' },
  { id: 'settings',       label: 'Settings',       icon: '⚙️',  href: '/control-center/settings' },
] as const

/**
 * The conditional Setup item, shown only while the client has open setup steps.
 *
 * 🔴 IT LIVES HERE WITH THE OTHERS ON PURPOSE (John, 2026-09-17). It was defined
 * inline in `App.tsx` with `icon: 'list-checks'` — a Lucide icon NAME, while
 * every item in this list is an EMOJI. The sidebar renders the icon slot as
 * text, so a client opening their Control Center saw the literal words
 * "list-checks" wrapped across two lines beside "Setup".
 *
 * ⚠️ Nothing could have caught it while the item lived somewhere else: it was
 * the only nav item outside this list, so it was the only one nothing compared
 * against. `theme.test.ts` now asserts every icon — this one included — is an
 * emoji rather than an identifier.
 */
export const CLIENT_SETUP_NAV_ITEM = {
  id: 'setup',
  label: 'Setup',
  // A compass: "where you are and what's next", which is what the page is.
  // Unused by any other item — 🏠 👥 ✉️ 💬 🗄️ ✅ 🤖 📋 📅 🔌 📊 🎁 ⚙️ are taken.
  icon: '🧭',
  href: '/control-center/setup',
} as const

// ─── Business Profile Categories ─────────────────────────────────────────────

/**
 * ⚠️ THE THIRD COPY OF THIS UNION (noted 2026-09-22). It also exists in
 * `backend/functions/src/schema/types.ts` (the owner) and, until today, as a
 * hand-written duplicate inside `ava/introCallScript.ts`. Adding 'voice' broke BOTH
 * duplicates, which is the only reason anyone learned they had drifted apart.
 * app/ cannot import backend/, so this copy has to exist — but when a category is
 * added, it is added HERE and in the schema, and `profileCategoryParity.test.ts`
 * reads the server's union to prove the two still agree.
 */
export type ProfileCategory =
  | 'services' | 'pricing' | 'policies' | 'licensing'
  | 'scheduling' | 'team' | 'voice' | 'service-area' | 'brand'

export const PROFILE_CATEGORY_LABELS: Record<ProfileCategory, { label: string; icon: string }> = {
  services:     { label: 'Services & Capabilities', icon: 'briefcase' },
  pricing:      { label: 'Pricing & Estimates',     icon: 'dollar-sign' },
  policies:     { label: 'Policies & Guarantees',   icon: 'shield' },
  licensing:    { label: 'Licensing & Insurance',    icon: 'award' },
  scheduling:   { label: 'Scheduling & Availability', icon: 'calendar' },
  team:         { label: 'Team & Operations',        icon: 'users' },
  voice:        { label: 'How Ava Says Things',       icon: 'message-circle' },
  'service-area': { label: 'Service Area',           icon: 'map-pin' },
  brand:        { label: 'Brand & Differentiators',  icon: 'star' },
}

// ─── Live Transfer Config ────────────────────────────────────────────────────

export type TransferTrigger = 'always' | 'request' | 'selective' | 'never'
export type TransferFallback = 'message' | 'book' | 'secondary' | 'voicemail'
export type TransferStyle = 'warm' | 'cold'

export const TRANSFER_TRIGGERS: Array<{ id: TransferTrigger; label: string; description: string }> = [
  { id: 'always',    label: 'Always During Business Hours', description: 'Every call during business hours gets a transfer attempt after qualification.' },
  { id: 'request',   label: 'Only When Caller Requests',   description: 'Phone Skill handles fully unless the caller asks to speak with a person.' },
  { id: 'selective', label: 'Selective — By Situation',      description: 'Transfer for emergencies, specific services, or high-value leads.' },
  { id: 'never',     label: 'Never — Always Take a Message', description: 'Phone Skill qualifies every caller and sends you a summary via SMS.' },
]

/**
 * The fallbacks a client can CHOOSE. 'book' and 'secondary' are RETIRED (John,
 * 2026-10-10): the live transfer never booked anyone or tried a second number, so
 * offering them promised something that did not happen. A stored legacy value is
 * shown and saved as 'message' (normalizeTransferFallback) — the server refuses the
 * retired values (backend ava/liveTransferEdit.ts).
 */
export type SettableTransferFallback = 'message' | 'voicemail'

export function normalizeTransferFallback(v: unknown): SettableTransferFallback {
  return v === 'voicemail' ? 'voicemail' : 'message'
}

export const TRANSFER_FALLBACKS: Array<{ id: SettableTransferFallback; label: string; description: string }> = [
  { id: 'message',   label: 'Return to Caller + Take Message', description: 'Phone Skill asks what they would like passed on and texts it to you.' },
  // ⚠️ Not "send to your voicemail": on a live transfer the caller is asked for a
  // message, exactly like 'message' — never put into your phone's voicemail.
  { id: 'voicemail', label: 'Take a Message (voicemail)',     description: 'Same as taking a message — Phone Skill asks what they would like passed on and texts it to you.' },
]

export const TRANSFER_STYLES: Array<{ id: TransferStyle; label: string; description: string }> = [
  { id: 'warm', label: 'Warm — IVR briefing before connect', description: 'Owner hears "You have a caller about [reason] — press 1 to accept" before being connected. (Phase 2: live in warm-mode IVR build.)' },
  { id: 'cold', label: 'Cold — Direct connect',              description: 'Phone Skill says "let me connect you" and bridges the call immediately.' },
]

// ─── Approval modes ──────────────────────────────────────────────────────────

export const APPROVAL_MODES = [
  { id: 'autopilot',     label: 'Autopilot',     description: 'Skill acts without approval. You get a weekly summary.' },
  { id: 'approve-each',  label: 'Review & Approve',  description: 'I bundle the day’s items into one review for your OK before anything goes out.' },
] as const

export type ApprovalMode = typeof APPROVAL_MODES[number]['id']

// ─── Utility ─────────────────────────────────────────────────────────────────

/** Format dollar amounts with $ prefix and comma separators */
export function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/** Format dollar amounts in dollars (not cents) */
export function formatDollars(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/** Format a date as MM/DD/YYYY per platform standard */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

/** Format a time as 12-hour with AM/PM and timezone per platform standard */
export function formatTime(date: Date | string, timezone = 'MT'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h = hours % 12 || 12
  const mm = String(minutes).padStart(2, '0')
  return `${h}:${mm} ${ampm} ${timezone}`
}
